"use client";
import React, { useEffect, useState, Suspense, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
// @ts-ignore
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// @ts-ignore
import { DRACOLoader } from "three-stdlib"; // 从 three-stdlib 导入 DRACOLoader
// @ts-ignore
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
// @ts-ignore
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
// @ts-ignore
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";

// @ts-ignore
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { Environment, OrbitControls, Html, Bounds, Loader } from "@react-three/drei";
import * as THREE from "three";
import { LoadStep } from "@/lib/utils";

// 动态选择合适的加载器
export const chooseLoader = (url: string) => {
	const ext = url.split(".").pop()?.toLowerCase();
	switch (ext) {
		case "gltf":
		case "glb":
			return GLTFLoader;
		case "3dm":
			return Rhino3dmLoader;
		case "obj":
			return OBJLoader;
		case "fbx":
			return FBXLoader;
		case "stl":
			return STLLoader;
		case "stp":
		case "step":
			return "stp"; // 保留 stp 文件的处理
		default:
			throw new Error(`Unsupported file format: ${url}`);
	}
};

// 缩放视图以适应3D对象并设置中心点
const zoomExtents = (
	object: THREE.Object3D,
	camera: THREE.Camera,
	controls: THREE.EventDispatcher | null,
	zoomed: boolean,
) => {
	if (zoomed) return; // 如果已经缩放过，则跳过
	const box = new THREE.Box3().setFromObject(object);
	const size = box.getSize(new THREE.Vector3());
	const center = box.getCenter(new THREE.Vector3());
	const maxDim = Math.max(size.x, size.y, size.z);
	const cameraZ = 1 / maxDim;
	object.scale.set(cameraZ, cameraZ, cameraZ);
	camera.position.set(center.x, center.y, center.y);
	camera.lookAt(center);

	if (controls) {
		// @ts-ignore
		controls?.target.copy(center); // 设置旋转中心为物体的中心
		// @ts-ignore
		controls?.update();
	}
};

// Model 组件：处理 3D 文件的下载、解析和渲染
const Model: React.FC<{
	modalUrl: string;
	fileName: string;
	arrayBuffer?: ArrayBuffer;
	metalness?: number;
	roughness?: number;
	color?: string;
	isHovered: boolean;
	scale?: number;
}> = ({
	scale = 1,
	modalUrl,
	fileName,
	arrayBuffer,
	metalness = 0.5,
	roughness = 0.5,
	color = "#cccccc",
	isHovered,
}) => {
	const [object, setObject] = useState<THREE.Object3D | null>(null);
	const [zoomed, setZoomed] = useState(false); // 新增 state 来跟踪是否已进行过缩放
	const { camera, controls } = useThree();

	const loadModel = async () => {
		try {
			let buffer = arrayBuffer;
			if (!buffer) {
				const response = await fetch(modalUrl);
				buffer = await response.arrayBuffer();
			}

			const loaderType = chooseLoader(fileName);
			let loadedObject;

			if (loaderType === GLTFLoader) {
				// 创建并设置 DracoLoader
				const dracoLoader = new DRACOLoader();
				dracoLoader.setDecoderPath("/draco/"); // 设置解码器路径（可以使用CDN或本地路径）

				const loader = new GLTFLoader();
				loader.setDRACOLoader(dracoLoader); // 将 Draco 解码器传递给 GLTFLoader

				loadedObject = await new Promise((resolve, reject) => {
					loader.parse(buffer, "", (object: any) => resolve(object.scene || object), reject);
				});
			} else if (loaderType === "stp") {
				// 处理 stp 格式的加载
				loadedObject = await LoadStep(modalUrl, buffer);
			} else {
				const loader = new loaderType();
				if (loader instanceof Rhino3dmLoader) {
					loader.setLibraryPath("/rhino3dm/");
				} else if (loader instanceof STLLoader) {
					const material = loader.parse(buffer as ArrayBuffer);
					loadedObject = new THREE.Mesh(
						material,
						new THREE.MeshStandardMaterial({
							color,
							metalness,
							roughness,
						}),
					);
				} else {
					loadedObject = await new Promise((resolve, reject) => {
						loader.parse(buffer, "", (object: any) => resolve(object.scene || object), reject);
					});
				}
			}
			// 应用缩放
			loadedObject.scale.set(scale, scale, scale);
			setObject(loadedObject);
			zoomExtents(loadedObject, camera, controls, zoomed);
			setZoomed(true); // 标记已缩放
		} catch (error) {
			console.error("Error loading model:", error);
		}
	};

	useEffect(() => {
		loadModel();
	}, [modalUrl, arrayBuffer]);

	useEffect(() => {
		if (metalness === 0.5 && roughness === 0.5 && (color === "#cccccc" || color === "#fafafa")) return;
		if (object) {
			object.traverse((child: any) => {
				if (child instanceof THREE.Mesh) {
					const material = child.material as THREE.Material;
					child.material = material.clone();
					child.material.color = new THREE.Color(color);
					child.material.metalness = metalness;
					child.material.roughness = roughness;
				}
			});
		}
	}, [metalness, roughness, color, object]);

	// 使用 useFrame 实现自动旋转
	useFrame((state, delta) => {
		if (object) {
			if (!isHovered) {
				object.rotation.y += delta * 0.4; // 控制旋转速度
			}
			const box = new THREE.Box3().setFromObject(object);
			const center = box.getCenter(new THREE.Vector3());
			object.position.sub(center); // 移动对象位置到几何体中心
		}
	});

	return object ? (
		<group>
			<primitive object={object} dispose={null} />
		</group>
	) : null;
};

// 主要组件：加载和渲染 3D 文件
const LoadNormalModal: React.FC<{
	modalUrl: string;
	className?: string;
	fileName: string;
	arrayBuffer?: ArrayBuffer;
	metalness?: number;
	roughness?: number;
	color?: string;
	scale?: number;
}> = ({ modalUrl, scale = 1, className, fileName, arrayBuffer, metalness, roughness, color }) => {
	const [isHovered, setIsHovered] = useState(false);

	return (
		<Canvas
			shadows
			className={` ${className || ""}`}
			onCreated={({ gl }) => {
				if (typeof window !== "undefined") {
					gl.setPixelRatio(Math.min(window.devicePixelRatio, 1.0));
				}
			}}
			onWheel={(e) => e.stopPropagation()}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* 环境光提供整体照明 */}
			<ambientLight intensity={0.4} />

			{/* 动态跟随光源 */}
			<FollowCameraLight />

			{color !== "#cccccc" && color !== "#fafafa" && (
				<Environment
					preset={null}
					background={false}
					path="/3DModals/"
					files={["st_fagans_interior_1k.hdr"]}
				/>
			)}

			{(color === "#cccccc" || color === "#fafafa") && (
				<Environment preset={null} background={false} path="/3DModals/" files={["potsdamer_platz_1k.hdr"]} />
			)}

			<Suspense
				fallback={
					<Html center>
						<Loader />
					</Html>
				}
			>
				<Bounds fit clip observe={false} margin={0.25} maxDuration={1}>
					<Model
						modalUrl={modalUrl}
						fileName={fileName}
						arrayBuffer={arrayBuffer}
						metalness={metalness}
						roughness={roughness}
						color={color}
						scale={scale}
						isHovered={isHovered}
					/>
				</Bounds>
			</Suspense>
			<OrbitControls
				makeDefault
				maxPolarAngle={Math.PI}
				enableZoom={true}
				minDistance={0.8}
				maxDistance={1}
			/>
		</Canvas>
	);
};

// 动态跟随光源组件
const FollowCameraLight = () => {
	const lightRef = useRef<THREE.DirectionalLight>(null);
	const cameraRef = useRef();

	useFrame(({ camera }) => {
		if (lightRef.current) {
			lightRef.current.position.copy(camera.position);
			lightRef.current.target.position.copy(new THREE.Vector3(0, 0, 0));
			lightRef.current.target.updateMatrixWorld();
		}
	});

	return <directionalLight ref={lightRef} intensity={0.8} color="#ffffff" />;
};

export default LoadNormalModal;
